import type {
  OpsNamespaceRefreshSkuNamesResponse,
  OpsNamespaceRenamePreflightResponse,
  OpsNamespaceRenameResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  getNamespaceRenamePreflight,
  refreshNamespaceSkuNames,
  renameNamespace,
} from './namespaces.ts';

/** Rename mutations invalidate the shared channels cache — a namespace IS a channel. */
const CHANNELS_KEY = ['channels'] as const;

export function useNamespaceRenamePreflight(
  id: string | undefined,
  enabled: boolean,
): UseQueryResult<OpsNamespaceRenamePreflightResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['namespace-rename-preflight', id],
    queryFn: async () => getNamespaceRenamePreflight(id ?? '', undefined, fetchImpl),
    enabled: enabled && id !== undefined,
  });
}

export function useRenameNamespace(): UseMutationResult<
  OpsNamespaceRenameResponse,
  Error,
  { id: string; slug: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, slug }) => renameNamespace(id, { slug }, fetchImpl),
    onSuccess: (_res, { id }) => {
      void qc.invalidateQueries({ queryKey: CHANNELS_KEY });
      // A follow-up rename within staleTime must not see pre-rename usage.
      void qc.invalidateQueries({ queryKey: ['namespace-rename-preflight', id] });
    },
  });
}

export function useRefreshNamespaceSkuNames(): UseMutationResult<
  OpsNamespaceRefreshSkuNamesResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  return useMutation({
    mutationFn: (id: string) => refreshNamespaceSkuNames(id, fetchImpl),
  });
}
